import React, { useCallback, useEffect } from 'react';
import { Card, H1, IconName, Intent } from '@blueprintjs/core';
import { useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../../../hooks';

import {
  WavelightStudy,
  WavelightsState,
  WavelightPerson,
  WavelightMeasurements,
} from '../types';

import {
  getWavelightItem,
  saveMeasurements,
  savePerson,
  saveStudy,
  setMeasurements,
  setPerson,
  setStudy,
} from '../reducer';

import CardItem from '../../../components/CardItem';
import PersonData from '../../../components/PersonData';
import StudyData from '../../../components/StudyData';
import Measurements from '../../../components/Measurements';
import Loading from '../../../components/Loading';

import Toaster from '../../../components/Toaster';

import { WavelightsContainer } from './styles';

const WavelightsItem = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { isLoading, wavelight }: WavelightsState = useAppSelector(
    (state) => state.wavelight,
  );

  // Toaster
  const showToasterMessage = useCallback(
    async (message: string, intent: Intent, icon: IconName) => {
      Toaster.show({
        message,
        intent,
        icon,
      });
    },
    [],
  );

  // Load Initial Item
  useEffect(() => {
    dispatch(getWavelightItem(id as string));
  }, [dispatch, id]);

  // Person Control
  const updatePerson = useCallback(
    (person: WavelightPerson) => {
      dispatch(setPerson(person));
    },
    [dispatch],
  );

  const saveNewPerson = useCallback(
    (person: WavelightPerson) => {
      showToasterMessage(
        `Dados pessoais salvo para: ${id}`,
        Intent.SUCCESS,
        'tick-circle',
      );
      dispatch(
        savePerson({
          id: id as string,
          person,
        }),
      );
    },
    [id, dispatch, showToasterMessage],
  );

  // Study Control
  const updateStudyData = useCallback(
    (value: WavelightStudy) => {
      dispatch(setStudy(value));
    },
    [dispatch],
  );

  const saveNewStudy = useCallback(
    (study: WavelightStudy) => {
      showToasterMessage(
        `Dados do estudo salvos para: ${id}`,
        Intent.SUCCESS,
        'tick-circle',
      );
      dispatch(
        saveStudy({
          id: id as string,
          study,
        }),
      );
    },
    [id, dispatch, showToasterMessage],
  );

  // Measurements Control
  const updateMeasurements = useCallback(
    (value: WavelightMeasurements) => {
      dispatch(setMeasurements(value));
    },
    [dispatch],
  );

  const saveNewMeasurements = useCallback(
    (measurements: WavelightMeasurements) => {
      showToasterMessage(
        `Medidas salvas para: ${id}`,
        Intent.SUCCESS,
        'tick-circle',
      );
      dispatch(
        saveMeasurements({
          id: id as string,
          measurements,
        }),
      );
    },
    [id, dispatch, showToasterMessage],
  );

  if (!wavelight || isLoading) {
    return <Loading message="Loading" />;
  }

  const measurementsWithIgnoreAll = {
    ...wavelight.measurements,
    ignore: {
      ...wavelight.measurements?.ignore,
      ignoreAll:
        wavelight.measurements?.ignore?.ignoreBpm &&
        wavelight.measurements?.ignore?.ignoreRespiratoryRate &&
        wavelight.measurements?.ignore?.ignorebloodO2Saturation &&
        wavelight.measurements?.ignore?.ignorebloodPreassure &&
        wavelight.measurements?.ignore?.ignoreGlucose,
    },
  };

  return (
    <WavelightsContainer>
      <Card style={{ width: '100%' }}>
        <H1>Dados Pessoais</H1>
        <PersonData
          person={wavelight.person}
          onChange={updatePerson}
          onSave={saveNewPerson}
        />
      </Card>
      <Card style={{ width: '100%' }}>
        <CardItem>
          <StudyData
            study={wavelight.study}
            onUpdateStudy={updateStudyData}
            onSaveStudy={saveNewStudy}
          />
        </CardItem>
      </Card>
      <Card style={{ width: '100%' }}>
        <CardItem>
          <Measurements
            title
            measurements={measurementsWithIgnoreAll}
            onMeasurementsUpdate={updateMeasurements}
            onMeasurementsSave={saveNewMeasurements}
          />
        </CardItem>
      </Card>
    </WavelightsContainer>
  );
};

export default WavelightsItem;
